import { Pressable, View } from "react-native"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { router, useLocalSearchParams } from "expo-router"

import { customerTransactionInput, CustomerTransactionType } from "@white-labels/input/src/m5azen"
import { objectValues } from "@white-labels/utils"

import { Button, DatePicker, Header, KeyboardAwareScrollView, ReceiptPicker, Text, TextInput } from "@src/components"
import { PriceInput } from "@src/components/price-input"
import { dayjs, sharedStyles, trpc } from "@src/utils"
import { ar } from "@src/i18n/ar"

export default function CustomerNewTransaction() {
  const params = useLocalSearchParams<{ id: string }>()
  const id = Number.parseInt(params?.id, 10)
  const trpcUtils = trpc.useUtils()

  const { control, handleSubmit, formState, watch, setValue } = useForm({
    resolver: zodResolver(customerTransactionInput),
    defaultValues: { customerId: id, type: "PAYMENT", date: dayjs().utc().unix(), method: "cash" },
  })

  const addTransaction = trpc.customers.addTransaction.useMutation()

  const onSubmit = handleSubmit((data) => {
    addTransaction.mutate(
      { ...data },
      {
        onSuccess() {
          trpcUtils.customers.get.invalidate({ id })
          trpcUtils.customers.transactions.invalidate({ id })
          router.back()
        },
        onError(error) {
          alert(error.message)
        },
      },
    )
  })

  const type = watch("type")
  const isPayment = type === "PAYMENT"

  return (
    <>
      <Header title={ar.addCustomerTransaction} />
      <KeyboardAwareScrollView contentContainerStyle={sharedStyles.contentContainer}>
        <View className="flex-row flex-wrap gap-2 mb-4">
          {objectValues(CustomerTransactionType).map((key) => {
            if (key === "ADJUSTMENT" || key === "REFUND") return null

            return (
              <Pressable className={`px-2 py-1 ${type === key ? "bg-blue-50 border-blue-500" : "bg-white border-gray-200"}`} key={key} onPress={() => setValue("type", key)}>
                <Text className={`text-center font-medium ${type === key ? "text-blue-600" : "text-gray-600"}`}>{ar[key]}</Text>
              </Pressable>
            )
          })}
        </View>

        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value } }) => (
            <View className="mb-3">
              <Text className="text-gray-500 mb-1 text-sm font-bold">{ar.amount}</Text>
              <PriceInput value={value ? Number(value) : undefined} onValueChange={onChange} autoFocus />
            </View>
          )}
        />

        {type === "ORDER_DEBT" && (
          <Controller
            control={control}
            name="receiptRef"
            render={({ field: { onChange, value } }) => (
              <>
                <Text className="text-gray-500 mb-1 text-sm font-bold">{ar.receipt}</Text>
                <ReceiptPicker id={value ? Number(value) : undefined} onChange={(id) => onChange(id ? String(id) : undefined)} />
              </>
            )}
          />
        )}

        <Controller
          control={control}
          name="date"
          render={({ field: { onChange, value } }) => (
            <View className="mb-3">
              <Text className="text-gray-500 mb-1 text-sm font-bold">{ar.date}</Text>
              <DatePicker mode="datetime" value={value ? dayjs.unix(value).local().toDate() : undefined} onChange={(date) => onChange(dayjs(date).unix())} />
            </View>
          )}
        />

        {isPayment && (
          <Controller
            control={control}
            name="method"
            render={({ field: { onChange, value } }) => (
              <View className="mb-3">
                <Text className="text-gray-500 mb-1 text-sm font-bold">{ar.paymentMethod}</Text>
                <View className="flex-row gap-2">
                  {([{ value: "cash" }, { value: "card" }, { value: "wallet" }] as const).map((option) => (
                    <Pressable
                      key={option.value}
                      onPress={() => onChange(option.value)}
                      className={`flex-1 py-2 rounded border ${value === option.value ? "bg-blue-50 border-blue-500" : "bg-white border-gray-200"}`}
                    >
                      <Text className={`text-center font-medium ${value === option.value ? "text-blue-600" : "text-gray-600"}`}>{ar[option.value]}</Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            )}
          />
        )}

        <Controller
          control={control}
          name="note"
          render={({ field: { onChange, value } }) => (
            <View className="mb-4">
              <Text className="text-gray-500 mb-1 text-sm font-bold">{ar.notes}</Text>
              <TextInput placeholder={ar.notes} value={value} onChangeText={onChange} className="bg-white" />
            </View>
          )}
        />

        <Button onPress={onSubmit} loading={addTransaction.status === "pending"} disabled={!formState.isValid}>
          {ar.confirm}
        </Button>
      </KeyboardAwareScrollView>
    </>
  )
}
